import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { ProjectCreateRequest, ProjectEditRequest, ProjectStylingRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useCreateProject = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: ProjectCreateRequest) => api.postProjectV1(data),
    async onSuccess() {
      showFlashToast({ message: t("page.projects.new.notification.success"), variant: "success" });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF_PROJECTS() });
    },
    onError() {
      showFlashToast({ message: t("page.projects.new.notification.failed"), variant: "alert" });
    },
  });
};

const useEditProject = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: ProjectEditRequest) => api.putProjectV1(projectId, data),
    onSuccess() {
      showFlashToast({ message: t("page.projects.edit.notification.success"), variant: "success" });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF(projectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF_PROJECTS() });
    },
    onError() {
      showFlashToast({ message: t("page.projects.edit.notification.failed"), variant: "alert" });
    },
  });
};

const useCloseProject = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (deleteUsers: boolean) =>
      api.deleteProjectByIdV1(projectId, {
        deleteUsers,
      }),
    onSuccess() {
      showFlashToast({ message: t("page.projects.close-project.notification.success"), variant: "success" });

      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT_DETAILS(projectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF_PROJECTS() });
    },
    onError() {
      showFlashToast({ message: t("page.projects.close-project.notification.failed"), variant: "alert" });
    },
  });
};

const useEditProjectStyling = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: ProjectStylingRequest) => api.putProjectStylingV1(projectId, data),
    onSuccess() {
      showFlashToast({ message: t("page.project-styling.notification.success"), variant: "success" });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF() });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF_PROJECTS() });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT_DETAILS(projectId) });
    },
    onError() {
      showFlashToast({ message: t("page.project-styling.notification.failed"), variant: "alert" });
    },
  });
};

export const projectMutations = {
  useCreateProject,
  useEditProject,
  useCloseProject,
  useEditProjectStyling,
};
